import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IconButton, Stack } from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PreviewIcon from "@mui/icons-material/Preview";

import { EditPartner } from "./EditPartner";
import { selectPartner } from "../../../store/partner/partnerSlice";
import { TableLayout } from "../../layout/TableLayout";
import { deletePartner } from "../../helpers/partner";

export const PartnersTable = ({ rows, setReloadData }) => {
  const { t } = useTranslation("global");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userToAction } = useSelector((state) => state.partner);
  const [openEdit, setOpenEdit] = useState(false);

  const columns = [
    {
      field: "fullName",
      headerName: t("partnerlist.columnName"),
      minWidth: 220,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) =>
        `${params.row.name || ""} ${params.row.surnames || ""}`,
    },
    {
      field: "idCrm",
      headerName: t("partnerlist.idCrm"),
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "email",
      headerName: t("partnerlist.columnEmail"),
      minWidth: 220,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "dni",
      headerName: t("partnerlist.columnDni"),
      minWidth: 140,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "typeDni",
      headerName: t("partnerlist.columnTypeDni"),
      minWidth: 140,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => t(`typeDni.${params.row.typeDni}`),
    },
    {
      field: "address",
      headerName: t("partnerlist.columnAddress"),
      minWidth: 320,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "zipCode",
      headerName: t("partnerlist.columnZipCode"),
      minWidth: 90,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "country",
      headerName: t("partnerlist.columnCountry"),
      minWidth: 130,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "phone",
      headerName: t("partnerlist.columnTel"),
      minWidth: 135,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "role",
      headerName: t("partnerlist.columnRole"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "investmentType",
      headerName: t("partnerlist.investmentType"),
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "club",
      headerName: t("partnerlist.columnClub"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "language",
      headerName: t("partnerlist.columnLanguage"),
      minWidth: 80,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "registrationDate",
      headerName: t("partnerlist.columnRegistrationDate"),
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "isActive",
      headerName: t("partnerlist.columnIsActive"),
      minWidth: 80,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) =>
        params.row.isActive
          ? t("partnerlist.booleanIsActive")
          : t("partnerlist.booleanIsInactive"),
    },
    {
      field: "urlDocuments",
      headerName: t("startupHeaders.inverterURL"),
      minWidth: 150,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <Stack direction="row" gap={0}>
            <a href={params.row.urlDocuments} target="_blank" rel="noreferrer">
              {t("link.link")}
            </a>
          </Stack>
        );
      },
    },
    {
      field: "action",
      headerName: t("partnerlist.columnActions"),
      maxWidth: 110,
      align: "center",
      headerAlign: "center",
      flex: 0,
      sortable: false,
      renderCell: (params) => {
        return (
          <Stack direction="row" gap={0}>
            <IconButton color="secondary" onClick={() => onViewClick(params)}>
              <PreviewIcon />
            </IconButton>
            <IconButton color="secondary" onClick={() => onEditClick(params)}>
              <EditRoundedIcon />
            </IconButton>
            <IconButton color="secondary" onClick={() => onDeleteClick(params)}>
              <DeleteOutlineRoundedIcon />
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  const onViewClick = (e) => {
    navigate(
      `/viewUserPositions?indiv=${e.id}&name=${e.row.name} ${e.row.surnames}&role=${e.row.role}`
    );
  };

  const onEditClick = (e) => {
    dispatch(selectPartner(e.row));
    setOpenEdit(true);
  };

  const onDeleteClick = async (e) => {
    await deletePartner(
      e,
      t("delete.successMsg"),
      t("delete.errorMsg"),
      t("delete.cancelButton"),
      t("delete.confirmButton"),
      t("delete.deleteConfirm")
    );
    setReloadData((prevState) => !prevState);
  };

  return (
    <>
      <TableLayout
        rows={rows.filter((dataUser) => dataUser.role !== "Admin")}
        columns={columns}
        title="partner"
      />
      {userToAction.name !== "" && openEdit && (
        <EditPartner setHidden={setOpenEdit} setReloadData={setReloadData} />
      )}
    </>
  );
};
